/* eslint-disable no-unused-vars */
/* eslint-disable import/extensions */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  USER_ROLES,
  UserUtilities,
  Constants,
} from '@pwc/pwc-deals-web-common';
import {
  getIsThirdPartyMember,
  getFakeClientUser,
  getUserRole,
  setActualUserRole,
  setUserRole,
} from 'common/redux/security/loggedInUser/loggedInUserSlice';

import { Redirect, useHistory, useParams, useLocation } from 'react-router-dom';
import featureFlags from 'common/helpers/featureFlags';
import authorizationService from 'common/services/security/authorizationService';
import { isUMEnabled } from 'features/project/projectUtil';
import CommentPanelContainer from '../../../common/components/CommentPanel/CommentPanelContainer.tsx';
import LoadingError from '../../../common/components/LoadingError';
import RibbonPanelContainer from '../../../common/components/RibbonPanel/RibbonPanelContainer';
import { isReportLivePreview } from '../../../common/helpers/featureFlags';
import userTokenService from '../../../common/services/security/userTokenService';
import { projectDisclaimerPath } from '../../../common/services/urlService';
import AnalysesContentRouter2 from '../../analyses/AnalysesContentRouter2';
import AnalysesGroupsContentRouter from '../../analysesGroups/components/AnalysesGroupsContentRouter/AnalysesGroupsContentRouter';
import ContentHubPageRouter from '../../contentHub/ContentHubRouter.tsx';
import DivestitureDiagnosticContentRouter from '../../divestitureDiagnostic/DivestitureDiagnosticContentRouter';
import FileUploadContentRouter from '../../fileUpload/FileUploadContentRouter';
import HypothesesContentRouter from '../../hypotheses/HypothesesContentRouter';
import InsightsContentRouter from '../../insights/InsightsContentRouter';
import NotificationsContentRouter from '../../notifications/NotificationsContentRouter/NotificationsContentRouter.tsx';
import ProjectDisclaimerRouter from '../../projectDisclaimer/ProjectDisclaimerRouter';
import {
  getProjectDisclaimerSelector,
  loadProjectDisclaimerThunk,
} from '../../projectDisclaimer/projectDisclaimerSlice';
import ProjectImportRouter from '../../projectImport/ProjectImportRouter.tsx';
import ReportsContentRouter from '../../reports/ReportsContentRouter';
import SettingsRouter from '../../settings/mfe/SettingsRouter.tsx';
import StructuredAnalysisRouter from '../../../mfe/structuredAnalysis/StructuredAnalysisRouter';
import ProjectTopicRouter from '../../projectsMFE/ProjectsRouter/ProjectTopicRouter';

import {
  getProjectError,
  getProjectErrorStatusCode,
  getProjectLoading,
  loadProjectThunk,
  getProject,
  getProjectSource,
} from '../projectSlice';
import './ProjectDetails.scss';
import ProjectOverviewContentRouter from './ProjectOverviewContentRouter';
import PerformanceTestPageRouter from '../../performanceTest/PerformanceTestRouter';
import PageHiddenAlert from '../../navigationCustomisation/PageHiddenAlert/PageHiddenAlert';
import UserManagementRouter from '../../userManagement/UserManagementRouter';

const isClientErrorCode = (code) => code >= 400 && code < 500;

const ProjectDetails = () => {
  const location = useLocation();
  const history = useHistory();
  const isThirdPartyMember = useSelector(getIsThirdPartyMember);
  const projectSource = useSelector(getProjectSource);

  if (isThirdPartyMember) {
    const thirPartyBlockedPaths = [
      '/notifications',
      '/settings',
      '/content-hub',
      '/team-members',
    ];
    thirPartyBlockedPaths.forEach((path) => {
      if (location.pathname.includes(path)) {
        history.push('/unauthorized');
      }
    });
  }
  const { projectId } = useParams();
  const isLoading = useSelector(getProjectLoading);
  const projectDisclaimer = useSelector(
    getProjectDisclaimerSelector(projectId)
  );
  const { users } = useSelector(getProject);
  const hasError = useSelector(getProjectError);
  const errorStatusCode = useSelector(getProjectErrorStatusCode);
  const dispatch = useDispatch();

  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const isFakeClientUser = useSelector(getFakeClientUser);

  const userEmail = authorizationService.getEmail();

  useEffect(() => {
    dispatch(loadProjectThunk(projectId));
    dispatch(loadProjectDisclaimerThunk(projectId));
  }, [dispatch, projectId]);

  if (hasError && isClientErrorCode(errorStatusCode)) {
    return null;
  }

  if (
    (hasError && !isClientErrorCode(errorStatusCode)) ||
    (projectDisclaimer && projectDisclaimer.error)
  ) {
    return <LoadingError />;
  }

  if (!isPwcUser) {
    if (!projectDisclaimer || projectDisclaimer.isLoading) {
      return null;
    }
    if (
      !projectDisclaimer.hasAccepted &&
      history.location.pathname !== projectDisclaimerPath(projectId) &&
      !isFakeClientUser
    ) {
      return (
        <Redirect
          to={{
            pathname: projectDisclaimerPath(projectId),
            state: { from: history.location.pathname },
          }}
        />
      );
    }
  }

  return (
    <>
      {!isThirdPartyMember && (
        <span id="comment-panel-container">
          {isReportLivePreview ? (
            <RibbonPanelContainer projectId={projectId} />
          ) : (
            <CommentPanelContainer projectId={projectId} />
          )}
        </span>
      )}

      <div className="page-content">
        {isLoading}
        <div className="router-content" data-testid="project-details-content">
          <PageHiddenAlert />
          {featureFlags.isPOTopicRefactorEnabled ? (
            <ProjectTopicRouter />
          ) : null}
          <ProjectDisclaimerRouter />
          <ProjectOverviewContentRouter />
          <HypothesesContentRouter />
          <InsightsContentRouter />
          <AnalysesContentRouter2 />
          <AnalysesGroupsContentRouter />
          <SettingsRouter />
          <ReportsContentRouter />
          <NotificationsContentRouter />
          <DivestitureDiagnosticContentRouter />
          <FileUploadContentRouter />
          <ContentHubPageRouter />
          <ProjectImportRouter />
          {isUMEnabled(projectSource, isThirdPartyMember) && (
            <UserManagementRouter />
          )}
          <StructuredAnalysisRouter />
          <PerformanceTestPageRouter />
        </div>
      </div>
    </>
  );
};
export default ProjectDetails;
